import api from './axios';
import {
  QboAccount,
  StripeBalances,
  CashflowMetricsResponse
} from './types'
import formatCurrency from '../utils/formatCurrency'

const dashboardApi = {

  getCashflowMetrics: async (business_id?: string): Promise<CashflowMetricsResponse> => {
    try {
      const config = business_id ? { params: { business_id } } : {};

      // New backend exposes summary, trend and forecast endpoints that use the
      // authenticated user to determine the business scope. Call them in
      // parallel and map into the existing CashflowMetricsResponse shape.
      const [summaryResp, trendResp, forecastResp] = await Promise.all([
        api.get<any>('/dashboard/cashflow/summary', config),
        api.get<any>('/dashboard/cashflow/trend', config),
        api.get<any>('/dashboard/cashflow/forecast', config),
      ]);

      const summary = summaryResp.data || {};
      const trend = trendResp.data || {};
      const forecast = forecastResp.data || {};
      const summary_trust_safe = summaryResp.data.summary_trust_safe || false;

      // Map daily trend to cash in/out 30-day shape expected by charts (default to '30' if available, else fallback)
      const trendDailyMap = trend.cash_in_vs_out_weekly || {};
      const daily30 = (trendDailyMap["30"] || []).map((d: any) => ({
        date: d.week_start,
        cashIn: d.cash_in ?? 0,
        cashOut: d.cash_out ?? 0,
      }));
      const daily60 = (trendDailyMap["60"] || []).map((d: any) => ({
        date: d.week_start,
        cashIn: d.cash_in ?? 0,
        cashOut: d.cash_out ?? 0,
      }));
      const daily90 = (trendDailyMap["90"] || []).map((d: any) => ({
        date: d.week_start,
        cashIn: d.cash_in ?? 0,
        cashOut: d.cash_out ?? 0,
      }));

      // Future Balance 30d (replaces older daily trend for the chart)
      const futureBalanceDaily = (summary.future_balance_30d_daily || []).map((d: any) => ({
        date: d.date,
        balance: d.balance ?? 0
      }));

      const futureBalanceWeekly30 = (summary.future_balance_30d_weekly || []).map((d: any) => ({
        week: d.week_end,
        balance: d.balance ?? 0
      }));

      const futureBalanceWeekly60 = (summary.future_balance_60d_weekly || []).map((d: any) => ({
        week: d.week_end,
        balance: d.balance ?? 0
      }));

      const futureBalanceWeekly90 = (summary.future_balance_90d_weekly || []).map((d: any) => ({
        week: d.week_end,
        balance: d.balance ?? 0
      }));

      // Forecast daily (fallback to daily_trend_30d if future_balance not present?)
      // Actually user says "change the daily trend chart to future_balance_30d_daily", so we prefer that.
      const forecastDaily = futureBalanceDaily.length > 0 ? futureBalanceDaily : (trend.daily_trend_30d || []).map((d: any) => ({
        date: d.date,
        balance: d.balance ?? d.current_balance ?? 0,
      }));

      // Weekly 13-week forecast mapping
      const weekly13 = (forecast.cash_outlook_13w_unconstrained || []).map((w: any) => ({
        week: w.week_ending,
        inflow: w.receipts ?? 0,
        outflow: (w.fixed_outflows ?? 0) + (w.flexible_outflows ?? 0) + (w.discretionary_outflows ?? 0),
        endBalance: w.ending_cash ?? 0,
      }));

      // Cash sources (best-effort from cashflow_calendar)
      const cashSources = (forecast.cashflow_calendar || []).map((c: any) => ({
        name: c.name || c.source || 'Item',
        value: c.amount ?? c.value ?? 0,
      }));

      // KPIs: summary.current_balance and summary.last_30d (Default)
      const currentBalanceVal = summary.current_balance ?? 0;

      const createKpiMetrics = (periodData: any) => ({
        current_balance: formatCurrency(currentBalanceVal),
        cash_in: formatCurrency(periodData?.cash_in ?? 0),
        cash_out: formatCurrency(periodData?.cash_out ?? 0),
        net_cash_flow: formatCurrency(periodData?.net_cash ?? 0),
      });

      const summary30d = createKpiMetrics(summary.last_30d);
      const summary60d = createKpiMetrics(summary.last_60d);
      const summary90d = createKpiMetrics(summary.last_90d);

      const kpi_metrics = summary30d; // Default

      const mapped: CashflowMetricsResponse = {
        kpi_metrics,
        cash_in_cash_out: {
          "30": daily30,
          "60": daily60,
          "90": daily90
        },
        forecast: forecastDaily,
        cash_sources: cashSources,
        weekly_forecast: weekly13,
        expense_categories: forecast.expense_categories ?? [],
        receivables: forecast.receivables ?? [],
        payables: forecast.payables ?? [],

        // Extended fields for switching
        summary_30d: summary30d,
        summary_60d: summary60d,
        summary_90d: summary90d,
        future_balance_30d: forecastDaily,

        // include raw backend fields for components that need them
        // (keeps backwards compatibility while exposing new shapes)
        // @ts-ignore - extend response with raw fields
        daily_trend_30d: trend.daily_trend_30d ?? [],
        // @ts-ignore
        daily_trend_90d: trend.daily_trend_90d ?? [],
        // @ts-ignore
        cash_in_vs_out_weekly: trend.cash_in_vs_out_weekly ?? [],
        // @ts-ignore
        // @ts-ignore
        cash_outlook_13w_unconstrained: forecast.cash_outlook_13w_unconstrained ?? [],
        // @ts-ignore
        cash_outlook_current_next_month_v1: forecast.cash_outlook_current_next_month_v1 ?? [],
        // @ts-ignore
        as_of_date: forecast.as_of_date ?? summary.as_of_date ?? null,
        // @ts-ignore
        forecast_snapshot_at: forecast.forecast_snapshot_at ?? null,
        // @ts-ignore
        forecast_snapshot_age_days: forecast.forecast_snapshot_age_days ?? 0,
        // @ts-ignore
        llm_validation_status: forecast.llm_validation_status ?? null,
        // @ts-ignore
        llm_validation_retries: forecast.llm_validation_retries ?? 0,
        // @ts-ignore
        llm_validation_concerns: forecast.llm_validation_concerns ?? [],
        // @ts-ignore
        cashflow_calendar: forecast.cashflow_calendar ?? [],
        // @ts-ignore
        quality_metrics: forecast.quality_metrics ?? undefined,
        // @ts-ignore
        future_balance_30d_weekly: summary.future_balance_30d_weekly ?? [],
        // @ts-ignore
        future_balance_60d_weekly: summary.future_balance_60d_weekly ?? [],
        // @ts-ignore
        future_balance_90d_weekly: summary.future_balance_90d_weekly ?? [],
        // @ts-ignore
        summary_trust_safe: summary_trust_safe,
      };

      console.log('cashflow metrics (mapped):', mapped);
      return mapped;
    } catch (error: any) {
      console.error('Failed to fetch cashflow metrics:', error);
      throw new Error('Failed to fetch cashflow metrics');
    }
  },


  // Cash Balances
  getQboAccounts: async (): Promise<{
    items: QboAccount[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> => {
    try {
      const response = await api.get('/qbo/accounts');
      console.log('accounts: ', response.data)
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch QBO accounts:', error);
      throw new Error('Failed to fetch QBO accounts');
    }
  },
  getStripeAccounts: async (): Promise<StripeBalances> => {
    try {
      const response = await api.get('/stripe/balances');
      console.log('Stripe balances: ', response.data)
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch Stripe accounts:', error);
      throw new Error('Failed to fetch Stripe accounts');
    }
  },
  // Trigger server-side calculation of cashflow metrics (hard refresh: recomputes forecast and runs LLM validation)
  // Uses 3 min timeout since LLM validation with retries can take 1–2 minutes
  calculateCashflow: async (business_id?: string, force_forecast_refresh: boolean = true): Promise<any> => {
    try {
      const params: Record<string, string | boolean> = { force_forecast_refresh };
      if (business_id) params.business_id = business_id;
      const response = await api.post('/dashboard/cashflow/calculate', null, {
        params,
        timeout: 180000, // 3 min for LLM validation pipeline
      });
      console.log('calculate cashflow response: ', response.data)
      return response.data;
    } catch (error: any) {
      console.error('Failed to calculate cashflow metrics:', error);
      throw new Error('Failed to calculate cashflow metrics');
    }
  },

  // LEGACY - DASHBOARD V1 ENDPOINTS ---------------------------------------
  // KPI Metrics (revenue, expenses, cashflow, runway)
  // getKpiMetrics: async (): Promise<KpiMetrics> => {
  //   try {
  //     const response = await api.get<KpiMetrics>('/dashboard/kpi-metrics');
  //     console.log('kpi: ', response.data)
  //     return response.data;
  //   } catch (error: any) {
  //     console.error('Failed to fetch KPI metrics:', error);
  //     throw new Error('Failed to fetch KPI metrics');
  //   }
  // },
  // Cashflow Forecast
  // getForecast: async (): Promise<ForecastResponse> => {
  //   try {
  //     const response = await api.get<ForecastResponse>('/intent_map/forecast');
  //     console.log('forecast: ', response.data)
  //     return response.data;
  //   } catch (error: any) {
  //     console.error('Failed to fetch forecast data:', error);
  //     throw new Error('Failed to fetch forecast data');
  //   }
  // },
  // Notifications
  // getNotifications: async (): Promise<NotificationsResponse> => {
  //   try {
  //     const response = await api.get<NotificationsResponse>('/notifications/alerts');
  //     console.log('notifications: ', response.data)
  //     return response.data;
  //   } catch (error: any) {
  //     console.error('Failed to fetch notifications data:', error);
  //     throw new Error('Failed to fetch notifications data');
  //   }
  // },
  // Upcoming Payments
  // getUpcomingPayments: async (): Promise<UpcomingPaymentsResponse> => {
  //   try {
  //     const response = await api.get<UpcomingPaymentsResponse>('/qbo/recurring-transactions');
  //     console.log('payments: ', response.data)
  //     return response.data;
  //   } catch (error: any) {
  //     console.error('Failed to fetch payments data:', error);
  //     throw new Error('Failed to fetch payments data');
  //   }
  // }
};

export default dashboardApi;
